// Selector de "gancho" (hook) para el modo "Video con influencer".
//
// Mobile: carrusel horizontal de cards verticales (9:16), mismo patrón que el
//   selector de modelos (`ModelPicker` / `HoverScrollCarousel`). Se renderiza
//   inline dentro de la `GanchoSection` (inyectada en el ConfigurePanel).
// Desktop: panel lateral a la derecha con scroll vertical (mismo chrome que
//   `VideoConfigSheet`), abierto desde la fila "Gancho".
//
// SCAFFOLD: la tabla de ganchos no existe todavía → cuando `GANCHOS` está
// vacío mostramos slots placeholder (9:16) solo para reservar el espacio.

import React from 'react';
import ReactDOM from 'react-dom';
import { Film, X } from 'lucide-react';
import { useExitTransition } from '../../../hooks/useExitTransition';
import {
  GANCHOS,
  GANCHO_PLACEHOLDER_SLOTS,
  type GanchoOption,
} from '../ganchosCatalog';

interface GanchoCardProps {
  gancho: GanchoOption | null; // null = placeholder
  selected: boolean;
  onSelect: () => void;
  /** Ancho fijo para el carrusel mobile; en desktop la card es fluida. */
  fixedWidth?: boolean;
}

const GanchoCard: React.FC<GanchoCardProps> = ({
  gancho,
  selected,
  onSelect,
  fixedWidth = false,
}) => {
  const isPlaceholder = gancho === null;
  return (
    <button
      type="button"
      disabled={isPlaceholder}
      onClick={onSelect}
      className={`${
        fixedWidth ? 'shrink-0 w-[150px]' : 'w-full'
      } aspect-[9/16] rounded-2xl overflow-hidden border-2 relative transition-colors ${
        selected ? 'border-brand-primary' : 'border-transparent'
      } ${isPlaceholder ? 'cursor-default' : ''}`}
    >
      {isPlaceholder ? (
        // Slot reservado: futuro clip vertical del gancho.
        <div className="absolute inset-0 bg-neutral-100 flex flex-col items-center justify-center gap-2 text-neutral-400">
          <Film className="w-7 h-7" strokeWidth={1.5} />
          <span className="text-[11px] font-medium">Próximamente</span>
        </div>
      ) : (
        <>
          {gancho.posterUrl || gancho.videoUrl ? (
            <video
              src={gancho.videoUrl}
              poster={gancho.posterUrl}
              muted
              loop
              playsInline
              className="absolute inset-0 w-full h-full object-cover"
            />
          ) : (
            <div className="absolute inset-0 bg-neutral-100" />
          )}
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/65 via-black/15 to-transparent px-3 py-2.5">
            <p className="text-left text-white text-[14px] font-semibold drop-shadow truncate">
              {gancho.nombre}
            </p>
          </div>
        </>
      )}
    </button>
  );
};

interface GanchoPickerProps {
  variant: 'mobile' | 'desktop';
  selectedId: string | null;
  onSelect: (id: string) => void;
  /** Solo desktop: controla la apertura del panel lateral. */
  open?: boolean;
  /** Solo desktop: cierra el panel lateral. */
  onClose?: () => void;
}

export const GanchoPicker: React.FC<GanchoPickerProps> = ({
  variant,
  selectedId,
  onSelect,
  open = false,
  onClose,
}) => {
  // Hook siempre al tope (las reglas de hooks no permiten llamarlo tras un
  // return condicional). En mobile no se usa `mounted`/`animated`.
  const { mounted, animated } = useExitTransition(open, 300);
  const hasGanchos = GANCHOS.length > 0;
  const placeholderSlots = Array.from({ length: GANCHO_PLACEHOLDER_SLOTS });

  // ── Mobile: carrusel horizontal inline ──────────────────────────────
  if (variant === 'mobile') {
    return (
      <div className="flex gap-3 overflow-x-auto -mx-5 px-5 scrollbar-hide">
        {hasGanchos
          ? GANCHOS.map((g) => (
              <GanchoCard
                key={g.id}
                gancho={g}
                selected={selectedId === g.id}
                onSelect={() => onSelect(g.id)}
                fixedWidth
              />
            ))
          : placeholderSlots.map((_, i) => (
              <GanchoCard
                key={`ph-${i}`}
                gancho={null}
                selected={false}
                onSelect={() => {}}
                fixedWidth
              />
            ))}
      </div>
    );
  }

  // ── Desktop: panel lateral con scroll vertical ──────────────────────
  if (!mounted) return null;

  return ReactDOM.createPortal(
    <>
      <div
        className={`fixed inset-0 z-[210] bg-black/40 transition-opacity duration-300 ${
          animated ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={onClose}
      />
      <div
        className={`fixed z-[211] bg-white flex flex-col shadow-2xl transition-transform duration-300 ease-out
          inset-x-0 bottom-0 rounded-t-[13px] max-h-[calc(100dvh-1rem-env(safe-area-inset-top,0px))]
          md:inset-x-auto md:left-auto md:top-4 md:right-4 md:bottom-4 md:h-auto md:w-[480px] md:rounded-[13px]
          ${
            animated
              ? 'translate-y-0 md:translate-x-0'
              : 'translate-y-full md:translate-y-0 md:translate-x-[calc(100%+32px)]'
          }`}
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 md:py-4 shrink-0">
          <h3 className="text-[16px] md:text-[17px] font-semibold text-brand-black/90 md:flex-1">
            Gancho
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="mx-5 h-px bg-black/[0.08] shrink-0" />

        {/* Lista vertical (grid 2 columnas) */}
        <div className="flex-1 overflow-y-auto px-5 py-5">
          <div className="grid grid-cols-2 gap-3">
            {hasGanchos
              ? GANCHOS.map((g) => (
                  <GanchoCard
                    key={g.id}
                    gancho={g}
                    selected={selectedId === g.id}
                    onSelect={() => onSelect(g.id)}
                  />
                ))
              : placeholderSlots.map((_, i) => (
                  <GanchoCard
                    key={`ph-${i}`}
                    gancho={null}
                    selected={false}
                    onSelect={() => {}}
                  />
                ))}
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
};
